import {
  DateString,
  getDateAsShortString,
  getFirstDayOfMonth,
  getMonthName,
} from '../../../../../common/src/date_string.ts';
import {
  Org,
  OrgType,
  RepeatKind,
  SongList,
  SongListEntry,
  SongListSchema,
  SongListSchemaEntry,
} from '../../../../../common/src/model.ts';
import {
  blankEntry,
  getSongListStartDate,
  getSundayOrdinal,
  getWeeklySongListType,
  isBlankEntry,
  isWeeklySongList,
  isWeeklySongListForDate,
  SundayOrdinal,
} from '../../../../../common/src/model_util.ts';
import {
  getReachEnd,
  getRepeatFrom,
  getRepeatKind,
  getRowEntries,
  RepeatingCell,
  resolveRepeatingCell,
} from '../../../../../common/src/repeating_rows.ts';
import { ensureUnreachable } from '../../../../../common/src/util.ts';
import { isSameShownEntry } from '../../../../../common/src/verses.ts';
import {
  findSongListEntry,
  getEmptyWeeklySongList,
  removeSongListEntry,
  replaceSongListEntry,
} from './song_list_rows.ts';

// The planner's cells for a repeating church liturgy row, the reach an edit shows through, and the song list writes
// behind editing a cell or clearing a column (issue #4191). The row's songs live in the Sundays' weekly lists;
// what a cell shows is common/src/repeating_rows.ts's fill: a Sunday before the fill start shows what it holds, and
// from the fill start on a Sunday that holds nothing shows the nearest earlier Sunday in the kind's scope. The fill
// start is the current Sunday, or the Sunday before it until that Sunday is written down on Monday (issue #4192), so a
// passed Sunday does not go blank in between. A Sunday before the row's repeatFrom (getRepeatFrom) is never filled.
//
// - Same every Sunday and Same all month draw runs: consecutive Sundays showing the same song, past or future, are one
//   cell. Consecutive Sundays from the current Sunday on that show nothing (holding a blank, or holding nothing no fill
//   reaches) are one empty cell too (issue #4201). A Same-all-month run, a song's or an empty one, never crosses a
//   month, so every cell of the row, song or empty, past or future, lies within one month and carries the month's tag
//   (NOV), which the planner keeps in view while it scrolls sideways (issue #4227). A Same-every-Sunday empty run goes
//   on across months and carries no tag. Before the current Sunday, whatever the fill start, a Sunday showing nothing
//   is a cell of its own and never joins the empty cell after it.
// - By Sunday of the month keeps one cell per Sunday. From the fill start (or the row's repeatFrom, when later) on each
//   carries its ordinal pill, filled when the Sunday holds something and outlined when it is filled in from an earlier
//   Sunday (inherited, drawn faded: a song with music in a faded purple, text in gray, issue #4202) or shows nothing.
//   Before it only the Sundays those cells are filled from (each ordinal's source) carry a pill, filled; every other
//   earlier Sunday is a plain cell. A cell's hover says where it repeats from and, from the current Sunday on, that
//   clicking sets every later Sunday of its ordinal; on a Sunday before the current one (the passed Sunday not yet
//   written down, or an earlier source) it promises nothing about later Sundays, since an edit there writes that Sunday
//   alone (issue #4211).
// - A blank (a hard delete) carries a mark at its Sunday (RepeatingRowCell.blankMarks, issue #4211) with a hover
//   saying the repeat stops there and Delete removes the blank: in a run row at its column within the empty cell,
//   whose own hover stays empty, and in a By-Sunday row on its cell, whose pill stays filled and whose hover is the
//   blank's. Only a blank Clear would remove as "Resume repeat" is marked (canWriteBlank: a church's repeating row, on
//   or after the current Sunday and the row's repeatFrom); a Sunday holding nothing, one a repeat has not reached, a
//   passed Sunday's blank (Delete does nothing there) and any blank in a row that does not repeat get none.
// - Editing writes one Sunday: its weekly list with the entry at the row. The one exception is an entry written into a
//   Same-all-month row on or after the current Sunday, which covers the rest of the month (issue #4210): every later
//   Sunday of the month also loses what it holds there of its own, so the fill shows the new entry to the month's end.
//   An entry pasted there covers the month the same way, except the later Sundays the same paste writes at the row
//   (withPasteRestOfMonthRemovals).
//   Clear removes the entry the Sunday holds (a soft delete, after which the fill reaches it again); on or after the
//   current Sunday, a Sunday holding nothing gets a blank (a hard delete, issue #4200), which shows nothing and stops
//   the fill (issue #4190). On a past Sunday Clear only removes, since a blank lives only in the future. Clear writes
//   one Sunday in every kind. The edit rules keep the current Sunday whatever the fill start.
// - A church's edit made while a passed Sunday is not yet written down (from the fill start up to the Sunday before the
//   current one) first writes those Sundays down and then applies the edit (issue #4209, planner_edits.ts), so the
//   edit stands: it is decided on the lists as the planner drew them and applied to the lists as written down. On such
//   a passed Sunday Clear removes what the cell shows, its own entry or the song the write-down puts there from the
//   fill, after which the Sunday, written down, shows nothing at the row (getClearEffect).
// Schools have no repeating rows: isRepeatingRow is false for them, and their columns clear as they always have.

// Whether the planner draws and writes a row of `kind` as a repeating row: only a church's rows repeat
export function isRepeatingKindFor(org: Org | undefined, kind: RepeatKind): boolean {
  return org?.type === OrgType.Church && kind !== RepeatKind.DoesNotRepeat;
}

export function isRepeatingRow(org: Org | undefined, row: SongListSchemaEntry | undefined): boolean {
  return isRepeatingKindFor(org, getRepeatKind(row));
}

const sundayOrdinalLabels = ['1st', '2nd', '3rd', '4th', '5th'];

export function getSundayOrdinalLabel(ordinal: SundayOrdinal): string {
  return sundayOrdinalLabels[ordinal - 1];
}

export interface RepeatingRowCell {
  columnStart: number; // first column (Sunday) of the cell
  columnCount: number; // columns it spans: 1 except for a run
  date: DateString; // the cell's first Sunday
  entry?: SongListEntry; // what the cell shows, never a blank
  isRun: boolean; // the row draws runs (Same every Sunday, Same all month), whatever this cell's length
  tag?: string; // the month tag (NOV) of every Same-all-month cell (issue #4227), or the ordinal pill ('1st' to '5th')
  tagOutlined?: boolean;
  isInherited?: boolean; // filled in from an earlier Sunday, drawn faded (see the module comment)
  // the hover text of the whole cell: a By-Sunday cell's (a blank's included), never a run cell's
  title?: string;
  // the Sundays within the cell holding a blank that stops the repeat (issue #4211), each drawn as a mark at its column
  // with its own hover text; absent when there are none, always in a row that does not repeat and before the current
  // Sunday (see the module comment)
  blankMarks?: RepeatingRowBlankMark[];
  // the list the cell shows: its first Sunday's weekly list, with the shown entry merged in at the row when the cell
  // is filled in from another Sunday
  displayList: SongList;
}

export interface RepeatingRowBlankMark {
  column: number; // the blank's Sunday as an offset within the cell, 0 for its first Sunday
  title: string; // the mark's hover text
}

// the hover text of a blank's mark in a Same-every-Sunday or Same-all-month row (issue #4211)
export const runBlankTitle = 'The repeat stops here. Delete removes the blank.';

// the hover text of a By-Sunday-of-the-month cell holding a blank, and of its mark (issue #4211)
export function getOrdinalBlankTitle(ordinal: SundayOrdinal): string {
  return (
    `The repeat stops here: later ${getSundayOrdinalLabel(ordinal)} Sundays show nothing until one is set. ` +
    'Delete removes the blank.'
  );
}

interface RowQuery {
  org: Org | undefined;
  kind: RepeatKind;
  repeatFrom?: DateString; // the first Sunday the row's kind applies to (getRepeatFrom, issue #4192)
  rowIndex: number;
  songLists: SongList[];
  weeklyDates: DateString[]; // the planner's columns, ascending
  currentWeekDate: DateString; // the current Sunday
  // the first Sunday the fill applies to on screen: the current Sunday, or the one before it while that Sunday is not
  // yet written down (getFillStartSunday, issue #4192)
  fillStartDate: DateString;
}

function findWeeklyList(org: Org | undefined, date: DateString, songLists: SongList[]): SongList | undefined {
  return songLists.find((list) => isWeeklySongListForDate(org, list, date));
}

// the org's weekly lists by Sunday, the first one for a Sunday as findWeeklyList finds it
export function getWeeklyListsByDate(org: Org | undefined, songLists: SongList[]): Map<DateString, SongList> {
  const byDate = new Map<DateString, SongList>();
  for (const list of songLists) {
    const date = getSongListStartDate(list);
    if (isWeeklySongList(org, list) && !byDate.has(date)) {
      byDate.set(date, list);
    }
  }
  return byDate;
}

// the cells of each of the schema's rows that repeat, by row index: what the planner draws for those rows. Every other
// row, and every row of a school, which has no repeating rows, is drawn as the Sundays' own plain cells, none of them
// filled in from another Sunday.
export function getRepeatingRowCellsByRow({
  rows,
  ...query
}: Omit<RowQuery, 'kind' | 'repeatFrom' | 'rowIndex'> & { rows: SongListSchemaEntry[] }): Map<
  number /* rowIndex */,
  RepeatingRowCell[]
> {
  const cellsByRow = new Map<number, RepeatingRowCell[]>();
  for (const row of rows) {
    if (!isRepeatingRow(query.org, row)) {
      continue;
    }
    cellsByRow.set(
      row.row,
      getRepeatingRowCells({ ...query, kind: getRepeatKind(row), repeatFrom: getRepeatFrom(row), rowIndex: row.row }),
    );
  }
  return cellsByRow;
}

// the row's cells in column order (see the module comment)
export function getRepeatingRowCells({
  org,
  kind,
  repeatFrom,
  rowIndex,
  songLists,
  weeklyDates,
  currentWeekDate,
  fillStartDate,
}: RowQuery): RepeatingRowCell[] {
  const entries = getRowEntries(rowIndex, songLists);
  const resolved = weeklyDates.map((date) =>
    resolveRepeatingCell({ kind, date, currentSunday: currentWeekDate, fillStart: fillStartDate, repeatFrom, entries }),
  );
  const weeklyLists = getWeeklyListsByDate(org, songLists);
  const displayListFor = (column: number): SongList => {
    const date = weeklyDates[column];
    const weeklyList = weeklyLists.get(date) ?? getEmptyWeeklySongList(org, date);
    const { entry, isSet } = resolved[column];
    return !isSet && entry ? replaceSongListEntry(weeklyList, rowIndex, { ...entry, row: rowIndex }) : weeklyList;
  };
  const plainCell = (column: number, isRun: boolean): RepeatingRowCell => ({
    columnStart: column,
    columnCount: 1,
    date: weeklyDates[column],
    entry: resolved[column].entry,
    isRun,
    displayList: displayListFor(column),
  });
  // the Sunday holds a blank that stops the repeat, which carries a mark (see the module comment)
  const holdsMarkedBlank = (column: number): boolean => {
    const date = weeklyDates[column];
    return holdsBlankStoppingRepeat({ ownEntry: entries.get(date), org, kind, repeatFrom, date, currentWeekDate });
  };
  switch (kind) {
    case RepeatKind.DoesNotRepeat:
      return weeklyDates.map((_date, column) => plainCell(column, false));
    case RepeatKind.BySundayOfMonth: {
      // a Sunday before the row's repeatFrom is never filled, so it is presented as a past Sunday is
      const pillStart = repeatFrom !== undefined && repeatFrom > fillStartDate ? repeatFrom : fillStartDate;
      const sourceDates = getOrdinalSourceDates(weeklyDates, resolved, pillStart);
      return weeklyDates.map((date, column) =>
        date < pillStart && !sourceDates.has(date)
          ? plainCell(column, false)
          : {
              ...plainCell(column, false),
              ...getOrdinalPresentation({
                date,
                cell: resolved[column],
                currentWeekDate,
                holdsMarkedBlank: holdsMarkedBlank(column),
              }),
            },
      );
    }
    case RepeatKind.SameEverySunday:
    case RepeatKind.SameAllMonth:
      return getRunColumns(kind, weeklyDates, resolved, currentWeekDate).map(({ columnStart, columnCount }) => {
        const cell: RepeatingRowCell = { ...plainCell(columnStart, true), columnCount };
        // every Same-all-month cell lies within one month, an empty one included (issue #4227)
        if (kind === RepeatKind.SameAllMonth) {
          cell.tag = getMonthName(cell.date).slice(0, 3).toUpperCase();
          cell.tagOutlined = false;
        }
        const blankMarks = Array.from({ length: columnCount }, (_, offset) => offset)
          .filter((offset) => holdsMarkedBlank(columnStart + offset))
          .map((offset) => ({ column: offset, title: runBlankTitle }));
        if (blankMarks.length > 0) {
          cell.blankMarks = blankMarks;
        }
        return cell;
      });
    default:
      ensureUnreachable(kind);
  }
}

// The Sundays before the pill start (the later of the fill start and the row's repeatFrom) that a
// By-Sunday-of-the-month cell from the pill start on shows its song from: each ordinal's source, which carries its
// filled pill. A blank fills no song, so a past blank is no source.
function getOrdinalSourceDates(
  weeklyDates: DateString[],
  resolved: RepeatingCell[],
  pillStart: DateString,
): Set<DateString> {
  const sourceDates = new Set<DateString>();
  weeklyDates.forEach((date, column) => {
    const { entry, sourceDate } = resolved[column];
    if (date >= pillStart && entry !== undefined && sourceDate !== undefined && sourceDate < pillStart) {
      sourceDates.add(sourceDate);
    }
  });
  return sourceDates;
}

// a run of consecutive columns the planner draws as one cell
interface ColumnRun {
  columnStart: number;
  columnCount: number;
}

// the column ranges of a run row's cells: consecutive columns showing the same song with the same verses (as the cell
// shows them), and consecutive columns from the current Sunday on showing nothing (issue #4201). In Same all month
// either run ends at the month's end, so every range lies within one month (issue #4227); a Same-every-Sunday empty run
// goes on across months. An empty run starts no earlier than the current Sunday, so a past Sunday showing nothing stays
// a cell of its own.
function getRunColumns(
  kind: RepeatKind,
  weeklyDates: DateString[],
  resolved: RepeatingCell[],
  currentWeekDate: DateString,
): ColumnRun[] {
  const runs: ColumnRun[] = [];
  weeklyDates.forEach((date, column) => {
    const run = runs.at(-1);
    if (run === undefined) {
      runs.push({ columnStart: column, columnCount: 1 });
      return;
    }
    const previous = resolved[run.columnStart].entry;
    const entry = resolved[column].entry;
    // a Same-all-month run, a song's or an empty one, ends at the month's end (issue #4227)
    const staysInMonth =
      kind !== RepeatKind.SameAllMonth || getFirstDayOfMonth(weeklyDates[run.columnStart]) === getFirstDayOfMonth(date);
    const continuesSongRun = previous !== undefined && entry !== undefined && isSameShownEntry(previous, entry);
    const continuesEmptyRun =
      previous === undefined && entry === undefined && weeklyDates[run.columnStart] >= currentWeekDate;
    if (staysInMonth && (continuesSongRun || continuesEmptyRun)) {
      run.columnCount += 1;
    } else {
      runs.push({ columnStart: column, columnCount: 1 });
    }
  });
  return runs;
}

// a By-Sunday-of-the-month cell's pill, hover text and blank mark, from the pill start on and on an earlier source
// Sunday (which is set, so its pill is filled with the set title). Before the current Sunday the hover makes no promise
// about later Sundays, since an edit there writes that Sunday alone (issue #4211).
function getOrdinalPresentation({
  date,
  cell: { entry, isSet, sourceDate, borrowsFirstSunday },
  currentWeekDate,
  holdsMarkedBlank,
}: {
  date: DateString;
  cell: RepeatingCell;
  currentWeekDate: DateString;
  holdsMarkedBlank: boolean; // the Sunday holds a blank that stops the repeat (see the module comment)
}): Pick<RepeatingRowCell, 'tag' | 'tagOutlined' | 'isInherited' | 'title' | 'blankMarks'> {
  const ordinalLabel = getSundayOrdinalLabel(getSundayOrdinal(date));
  const isInherited = !isSet && entry !== undefined;
  const isPast = date < currentWeekDate;
  const pill = { tag: ordinalLabel, tagOutlined: !isSet, isInherited };
  if (holdsMarkedBlank) {
    const blankTitle = getOrdinalBlankTitle(getSundayOrdinal(date));
    return { ...pill, title: blankTitle, blankMarks: [{ column: 0, title: blankTitle }] };
  }
  return { ...pill, title: getOrdinalTitle() };

  function getOrdinalTitle(): string | undefined {
    if (!entry) {
      return withPromise(undefined, 'set');
    }
    if (isSet) {
      return `Set on this Sunday. Later ${ordinalLabel} Sundays follow it until one is set again.`;
    }
    const source = sourceDate ? getDateAsShortString(sourceDate) : '';
    if (borrowsFirstSunday) {
      return withPromise(
        `No ${ordinalLabel} Sunday set yet, so it borrows the ${getSundayOrdinalLabel(1)} Sunday's entry from ` +
          `${source}.`,
        'set',
      );
    }
    return withPromise(`Repeats from ${source}.`, 'change');
  }

  // `text` followed by the promise that clicking sets every later Sunday of the ordinal, which a Sunday before the
  // current one leaves out (undefined when there is no text before it)
  function withPromise(text: string | undefined, verb: 'set' | 'change'): string | undefined {
    if (isPast) {
      return text;
    }
    const promise = `Click to ${verb} every ${ordinalLabel} Sunday from here on.`;
    return text === undefined ? promise : `${text} ${promise}`;
  }
}

// The columns an edit of the cell at `column` shows through, for the planner's reach outline (see getReachEnd): in a
// Same-every-Sunday row from `column` up to the Sunday before the next one that holds something, in a Same-all-month
// row to the month's last Sunday, which the edit writes through (getRepeatingCellWrites), each within the planner's
// columns. The reach is an edit rule, so it keeps the current Sunday: a passed Sunday, the one the fill still covers
// included, edits alone, and so does a Sunday before the row's repeatFrom, as its write does.
export function getSelectionReach({
  kind,
  repeatFrom,
  rowIndex,
  column,
  songLists,
  weeklyDates,
  currentWeekDate,
}: Omit<RowQuery, 'org' | 'fillStartDate'> & { column: number }): {
  columnStart: number;
  columnCount: number;
} {
  const date = weeklyDates[column];
  const lastSunday = weeklyDates.at(-1);
  if (date === undefined || lastSunday === undefined) {
    return { columnStart: column, columnCount: 1 };
  }
  const end = getReachEnd({
    kind,
    date,
    currentSunday: currentWeekDate,
    repeatFrom,
    entries: getRowEntries(rowIndex, songLists),
    lastSunday,
  });
  const columnCount = weeklyDates.slice(column).filter((weeklyDate) => weeklyDate <= end).length;
  return { columnStart: column, columnCount: Math.max(columnCount, 1) };
}

// Whether Clear may write a blank at a row of `kind` on `date` (a hard delete, issue #4200): only in a church's
// repeating row, and only on a Sunday on or after both the current Sunday (a blank lives only in the future) and the
// row's repeatFrom (getRepeatFrom), since a Sunday before it is never filled and a blank there would stop nothing
export function canWriteBlank({
  org,
  kind,
  repeatFrom,
  date,
  currentWeekDate,
}: {
  org: Org | undefined;
  kind: RepeatKind;
  repeatFrom?: DateString; // getRepeatFrom
  date: DateString;
  currentWeekDate: DateString;
}): boolean {
  return isRepeatingKindFor(org, kind) && date >= currentWeekDate && (repeatFrom === undefined || date >= repeatFrom);
}

// Whether the Sunday `date`, holding `ownEntry` at the row (undefined for nothing), holds a blank that stops the repeat
// (issue #4211): a blank on a Sunday where canWriteBlank allows one. The one rule behind both a blank's mark
// (RepeatingRowCell.blankMarks) and the cell menu's "Resume repeat" (getClearLabel); a blank anywhere else (a passed
// Sunday's, or one in a row that does not repeat) stops nothing Clear would resume
function holdsBlankStoppingRepeat({
  ownEntry,
  ...blankQuery
}: Parameters<typeof canWriteBlank>[0] & { ownEntry: SongListEntry | undefined }): boolean {
  return isBlankEntry(ownEntry) && canWriteBlank(blankQuery);
}

/**
 * What Clear (Delete, Backspace, the menu's Clear item) does to the row's cell on `date`, given that Sunday's weekly
 * list and what the cell shows there as the planner drew it: 'remove' when the Sunday holds an entry at the row, a
 * song, text or a blank (a soft delete, after which a repeating row fills the Sunday in again); 'blank' when it holds
 * nothing there (showing a filled-in song or nothing at all) and canWriteBlank allows one (a hard delete, which stops
 * the fill from there, issue #4200); undefined when Clear changes nothing. So Clear on one Sunday toggles: its song
 * goes, then a blank is written, then the blank goes. A Sunday where canWriteBlank does not allow a blank (before the
 * current Sunday or the row's repeatFrom, and every Sunday of a row that does not repeat, a school's included) only
 * ever loses its entry.
 *
 * A church's repeating row on a passed Sunday not yet written down (from `fillStartDate` up to the Sunday before the
 * current one, issue #4209) goes by what the cell shows instead, since the edit first writes that Sunday down, which
 * puts the shown entry into it: 'remove' when it shows an entry, its own or one filled in from an earlier Sunday, which
 * Clear then takes off the Sunday as written down, so it holds and shows nothing at the row; undefined when it shows
 * nothing, a blank it holds included, which the write-down moves on to the next Sunday in scope (a passed Sunday
 * holding nothing has nothing to clear). The one decision behind both the write (getRepeatingCellWrites) and whether
 * the cell menu offers Clear and what it calls it (getClearLabel). It builds nothing and resolves nothing: the menu
 * passes the entry of the cell it drew (RepeatingRowCell.entry: a run's Sundays all show an entry, or, on or after the
 * current Sunday only, all show nothing), and the write resolves the one cell edited the way the planner draws it
 * (getShownEntry), so the menu can ask it for every cell on every render.
 */
export function getClearEffect({
  org,
  kind,
  repeatFrom,
  rowIndex,
  date,
  weeklyList,
  shownEntry,
  currentWeekDate,
  fillStartDate,
}: {
  org: Org | undefined;
  kind: RepeatKind;
  repeatFrom?: DateString; // getRepeatFrom
  rowIndex: number;
  date: DateString;
  weeklyList: SongList | undefined; // the Sunday's weekly list, as getWeeklyListsByDate finds it
  // what the cell shows on `date` as the planner drew it, never a blank (RepeatingRowCell.entry, getShownEntry); read
  // only on a passed Sunday not yet written down
  shownEntry: SongListEntry | undefined;
  currentWeekDate: DateString;
  fillStartDate: DateString; // the first Sunday the fill applies to on screen (getFillStartSunday)
}): 'remove' | 'blank' | undefined {
  if (isRepeatingKindFor(org, kind) && date >= fillStartDate && date < currentWeekDate) {
    return shownEntry === undefined ? undefined : 'remove';
  }
  if (weeklyList && findSongListEntry(weeklyList, rowIndex)) {
    return 'remove';
  }
  if (canWriteBlank({ org, kind, repeatFrom, date, currentWeekDate })) {
    return 'blank';
  }
  return undefined;
}

// the cell menu's Clear item, named from what pressing it does (issue #4211)
export type ClearLabel = 'Clear cell' | 'Stop repeat here' | 'Resume repeat';

/**
 * The cell menu's name for Clear on the row's cell on `date` (issue #4211), from getClearEffect's decision and what the
 * Sunday holds at the row: "Stop repeat here" when Clear writes a blank, "Resume repeat" when it removes a blank that
 * stops the fill (one on a Sunday where canWriteBlank allows a blank), "Clear cell" when it removes the song or text
 * the cell shows, and undefined when Clear writes nothing, so the menu offers no Clear. A row that does not repeat, a
 * school's included, a Sunday before the current one (the passed Sunday not yet written down too) and a Sunday before
 * the row's repeatFrom never get a blank, so their Clear only ever removes and is always "Clear cell".
 */
export function getClearLabel(query: Parameters<typeof getClearEffect>[0]): ClearLabel | undefined {
  const effect = getClearEffect(query);
  if (effect === undefined) {
    return undefined;
  }
  if (effect === 'blank') {
    return 'Stop repeat here';
  }
  const { weeklyList, rowIndex } = query;
  const ownEntry = weeklyList && findSongListEntry(weeklyList, rowIndex);
  return holdsBlankStoppingRepeat({ ...query, ownEntry }) ? 'Resume repeat' : 'Clear cell';
}

// what the row's cell on `date` shows, as getRepeatingRowCells draws it (resolveRepeatingCell over the row's entries in
// `songLists`, the lists as the planner drew them): getClearEffect's `shownEntry` for the one cell an edit clears
function getShownEntry({
  kind,
  repeatFrom,
  rowIndex,
  date,
  songLists,
  currentWeekDate,
  fillStartDate,
}: Omit<RowQuery, 'org' | 'weeklyDates'> & { date: DateString }): SongListEntry | undefined {
  return resolveRepeatingCell({
    kind,
    date,
    currentSunday: currentWeekDate,
    fillStart: fillStartDate,
    repeatFrom,
    entries: getRowEntries(rowIndex, songLists),
  }).entry;
}

/**
 * The weekly lists to write for setting the row's cell on `date` to `entry`, or (with `entry` undefined) for clearing
 * it as getClearEffect decides: none when nothing changes. An entry is always written, verses edits included (an edit
 * to the entry the cell already holds is dropped before this, in SongCell). A cell of a church's row that does not
 * repeat is written the same way: its Sunday alone, and Clear only removes. Choose verses on a filled-in (projected)
 * Sunday therefore writes that Sunday as its own entry, #4191's rule that an edit writes the clicked Sunday: in a
 * By-Sunday-of-the-month row it becomes its ordinal's source from there on, in a Same-every-Sunday run the run splits
 * at it, and in a church's Same-all-month row on or after the current Sunday it covers the rest of the month as the
 * paragraph below says (one before the row's repeatFrom splits the month's run there instead, and a passed Sunday the
 * fill still covers edits alone, written down first as on any other passed Sunday).
 *
 * The edit is decided on `songLists`, the lists as the planner drew them, and built on `writtenSongLists`, the same
 * lists with the passed Sundays not yet written down written down (issue #4209, planner_edits.ts; `songLists` when
 * there are none): an entry lands in the Sunday's list as written down, which keeps every other row's written fill;
 * 'remove' takes whatever the written-down Sunday holds at the row off it (nothing to write when it holds nothing), and
 * 'blank' writes a blank at the row of the written-down list.
 *
 * Writing an entry into a church's Same-all-month row on or after the current Sunday covers the rest of the month
 * (issue #4210): the Sunday's list gets the entry, and the list of every later Sunday of the month that holds an entry
 * of its own at the row (a song, a text or a blank) loses it (getRestOfMonthLists), so the fill shows the new entry
 * from `date` to the month's end as one cell. A later list left empty is deleted by the write layer
 * (normalizeChurchWeeklyListWrites), as any emptied church Sunday is. Earlier Sundays of the month are unchanged.
 */
export function getRepeatingCellWrites({
  org,
  kind,
  repeatFrom,
  rowIndex,
  date,
  entry,
  songLists,
  writtenSongLists = songLists,
  currentWeekDate,
  fillStartDate,
}: {
  org: Org | undefined;
  kind: RepeatKind;
  repeatFrom?: DateString; // the first Sunday the row's kind applies to (getRepeatFrom, issue #4192)
  rowIndex: number;
  date: DateString;
  entry: SongListEntry | undefined;
  songLists: SongList[]; // as the planner drew them
  writtenSongLists?: SongList[]; // as written down (see above)
  currentWeekDate: DateString;
  fillStartDate: DateString; // getFillStartSunday
}): SongList[] {
  const writtenList = findWeeklyList(org, date, writtenSongLists);
  if (entry) {
    return [
      replaceSongListEntry(writtenList ?? getEmptyWeeklySongList(org, date), rowIndex, { ...entry, row: rowIndex }),
      ...getRestOfMonthLists({
        org,
        kind,
        repeatFrom,
        rowIndex,
        date,
        songLists: writtenSongLists,
        currentWeekDate,
      }).map((list) => removeSongListEntry(list, rowIndex)),
    ];
  }
  // Clear stays one Sunday in every kind, Same all month included: it toggles that Sunday's entry and blank (#4200)
  const effect = getClearEffect({
    org,
    kind,
    repeatFrom,
    rowIndex,
    date,
    weeklyList: findWeeklyList(org, date, songLists),
    shownEntry: getShownEntry({ kind, repeatFrom, rowIndex, date, songLists, currentWeekDate, fillStartDate }),
    currentWeekDate,
    fillStartDate,
  });
  if (effect === 'remove' && writtenList && findSongListEntry(writtenList, rowIndex)) {
    return [removeSongListEntry(writtenList, rowIndex)];
  }
  if (effect === 'blank') {
    return [replaceSongListEntry(writtenList ?? getEmptyWeeklySongList(org, date), rowIndex, blankEntry(rowIndex))];
  }
  return [];
}

// The lists of the Sundays after `date` in its month that an entry written on `date` takes the row's own entry off
// (getRepeatingCellWrites, withPasteRestOfMonthRemovals), in date order, as they are: each holds an entry at the row.
// From the row's weekly lists rather than the planner's columns, so the month is covered when the planner ends within
// it. Only a church's Same-all-month row covers the month: an edit in a Same-every-Sunday or By-Sunday-of-the-month
// row writes one Sunday, as #4191 specified.
// A passed `date` edits alone, since the edit rules keep the current Sunday (see the module comment of
// common/src/repeating_rows.ts): its later Sundays keep what they hold. One not yet written down is written down first
// (issue #4209), so the entry stands there as on any other past Sunday. A `date` before the row's repeatFrom (a
// repeatFrom later than the current Sunday, as a clock or time-zone difference can give) edits alone too: the Sundays
// between are not filled, so taking their entries off would empty them rather than show the new entry. getReachEnd
// draws the same two Sundays alone.
function getRestOfMonthLists({
  org,
  kind,
  repeatFrom,
  rowIndex,
  date,
  songLists,
  currentWeekDate,
}: Omit<RowQuery, 'weeklyDates' | 'fillStartDate'> & { date: DateString }): SongList[] {
  if (
    !isRepeatingKindFor(org, kind) ||
    kind !== RepeatKind.SameAllMonth ||
    date < currentWeekDate ||
    (repeatFrom !== undefined && date < repeatFrom)
  ) {
    return [];
  }
  const firstOfMonth = getFirstDayOfMonth(date);
  return [...getWeeklyListsByDate(org, songLists)]
    .filter(
      ([laterDate, list]) =>
        laterDate > date &&
        getFirstDayOfMonth(laterDate) === firstOfMonth &&
        findSongListEntry(list, rowIndex) !== undefined,
    )
    .sort(([lhs], [rhs]) => (lhs < rhs ? -1 : 1))
    .map(([, list]) => list);
}

/**
 * The weekly lists a paste of `cells` writes, built on `songLists`: each pasted Sunday's weekly list (the org's weekly
 * type starting on that Sunday, or a new one) with the cells' entries set at their rows, one list per Sunday, and the
 * rest-of-month removals of withPasteRestOfMonthRemovals. A church's paste while a passed Sunday is not yet written
 * down is built on the lists as written down (issue #4209, planner_edits.ts), so a pasted Sunday keeps every other
 * row's written fill.
 */
export function getPasteWrites({
  org,
  rows,
  cells,
  songLists,
  currentWeekDate,
}: {
  org: Org | undefined;
  rows: SongListSchemaEntry[]; // the schema's rows
  cells: { row: number; date: DateString; entry: SongListEntry }[]; // the pasted entries, in paste order
  songLists: SongList[];
  currentWeekDate: DateString;
}): SongList[] {
  let pastedLists: SongList[] = [];
  for (const { row, date, entry } of cells) {
    // TODO(hewitt): handle copy/paste for hymn of the month cells (rectangular selection)
    //               currently filtering on the weekly song list type
    const matchingSongList = (list: SongList) =>
      getSongListStartDate(list) === date && list.type === getWeeklySongListType(org);
    const songList =
      pastedLists.find(matchingSongList) ?? songLists.find(matchingSongList) ?? getEmptyWeeklySongList(org, date);
    const pastedList = { ...songList, songs: [...songList.songs.filter((song) => song.row !== row), entry] };
    pastedLists = [...pastedLists.filter((list) => getSongListStartDate(list) !== date), pastedList];
  }
  return withPasteRestOfMonthRemovals({
    org,
    rows,
    pastedCells: cells.map(({ row, date }) => ({ row, date })),
    pastedLists,
    songLists,
    currentWeekDate,
  });
}

/**
 * The weekly lists a paste writes: `pastedLists`, and for each pasted cell of a church's Same-all-month row the later
 * Sundays of its month taken off the row as an edit of that cell takes them (getRestOfMonthLists, issue #4210), except
 * the Sundays the same paste writes at the row, which keep what was pasted. A paste across a month therefore lands
 * every pasted value and clears the row on the month's Sundays after the last pasted one. A later Sunday whose list
 * the paste writes for another row loses the entry from that pasted list. The edit-alone exceptions are the edit's: a
 * passed pasted Sunday, and one before the row's repeatFrom, clear nothing. Every other row, and every row of a
 * school, writes only its pasted Sundays, so `pastedLists` comes back as it is.
 */
export function withPasteRestOfMonthRemovals({
  org,
  rows,
  pastedCells,
  pastedLists,
  songLists,
  currentWeekDate,
}: {
  org: Org | undefined;
  rows: SongListSchemaEntry[]; // the schema's rows
  pastedCells: { row: number; date: DateString }[]; // the cells the paste writes an entry into (not its empty cells)
  pastedLists: SongList[]; // each pasted Sunday's weekly list with its pasted entries, one per Sunday
  songLists: SongList[]; // the org's lists before the paste
  currentWeekDate: DateString;
}): SongList[] {
  const writes = new Map(pastedLists.map((list) => [getSongListStartDate(list), list]));
  const isPasted = (row: number, date: DateString) =>
    pastedCells.some((cell) => cell.row === row && cell.date === date);
  for (const { row: rowIndex, date } of pastedCells) {
    const row = rows.find((schemaRow) => schemaRow.row === rowIndex);
    const laterLists = getRestOfMonthLists({
      org,
      kind: getRepeatKind(row),
      repeatFrom: getRepeatFrom(row),
      rowIndex,
      date,
      songLists,
      currentWeekDate,
    });
    for (const laterList of laterLists) {
      const laterDate = getSongListStartDate(laterList);
      if (!isPasted(rowIndex, laterDate)) {
        writes.set(laterDate, removeSongListEntry(writes.get(laterDate) ?? laterList, rowIndex));
      }
    }
  }
  return [...writes.values()];
}

/**
 * The weekly list to write for clearing the column of `date` (Backspace or Delete on a selected column), or undefined
 * when there is nothing to write. It toggles as Clear on one cell does (getClearEffect, issue #4200): a church
 * Sunday on or after the current one that holds any entry, a blank included, is emptied (a soft delete, after which
 * its repeating rows fill in again), and one holding none gets a blank for every repeating row that shows something
 * there and whose blank canWriteBlank allows (a hard delete, which stops each of those fills there), or nothing when
 * no such row exists. So the first press empties the Sunday, the second blanks the rows the fill reaches so that the
 * Sunday shows nothing, and the third removes those blanks. A row showing nothing gets no blank, unlike Clear on its
 * one cell: the column's blanks are there to make the Sunday show nothing. Every other Sunday's list, a past one's and
 * a school's included, is emptied. The write layer deletes an emptied church list.
 *
 * A church's clear is decided on `songLists`, the lists as the planner drew them, and built on `writtenSongLists`, the
 * same lists with the passed Sundays not yet written down written down (issue #4209, planner_edits.ts; `songLists` when
 * there are none). A passed Sunday's written-down list is emptied whole, the fill just written into it included: the
 * admin wiped the Sunday, which, written down, then holds and shows nothing (nothing to write when the write-down left
 * it no list). From the current Sunday on, emptying takes off the rows the Sunday held as drawn and keeps a blank the
 * write-down moved onto it, which goes on stopping the fill there as the passed blank did on screen; the blanks are
 * those the drawn Sunday chose, written into its written-down list.
 */
export function getColumnClearWrite({
  org,
  schema,
  date,
  songLists,
  writtenSongLists = songLists,
  currentWeekDate,
}: {
  org: Org | undefined;
  schema: SongListSchema | undefined;
  date: DateString;
  songLists: SongList[]; // as the planner drew them
  writtenSongLists?: SongList[]; // as written down (see above)
  currentWeekDate: DateString;
}): SongList | undefined {
  const weeklyList = findWeeklyList(org, date, songLists);
  if (org?.type !== OrgType.Church) {
    return weeklyList && { ...weeklyList, songs: [] };
  }
  const writtenList = findWeeklyList(org, date, writtenSongLists);
  if (date < currentWeekDate) {
    return writtenList && { ...writtenList, songs: [] };
  }
  if (weeklyList && weeklyList.songs.length > 0) {
    const heldRows = new Set(weeklyList.songs.map(({ row }) => row));
    return writtenList && { ...writtenList, songs: writtenList.songs.filter(({ row }) => !heldRows.has(row)) };
  }
  const blanks = (schema?.rows ?? [])
    .filter((row) => {
      const kind = getRepeatKind(row);
      const repeatFrom = getRepeatFrom(row);
      if (!canWriteBlank({ org, kind, repeatFrom, date, currentWeekDate })) {
        return false;
      }
      const entries = getRowEntries(row.row, songLists);
      return (
        resolveRepeatingCell({ kind, date, currentSunday: currentWeekDate, repeatFrom, entries }).entry !== undefined
      );
    })
    .map(({ row }) => blankEntry(row))
    .sort((lhs, rhs) => lhs.row - rhs.row);
  if (blanks.length === 0) {
    return undefined;
  }
  const blankRows = new Set(blanks.map(({ row }) => row));
  const list = writtenList ?? getEmptyWeeklySongList(org, date);
  return {
    ...list,
    songs: [...list.songs.filter(({ row }) => !blankRows.has(row)), ...blanks].sort((lhs, rhs) => lhs.row - rhs.row),
  };
}
